import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, ChevronRight, ChevronDown, Rocket, X, Loader2, RotateCcw } from "lucide-react";
import { V } from "@/lib/vocab";

const PHASE_ACCENT = {
  setup: "bg-sky-500",
  learn: "bg-violet-500",
  marketing: "bg-amber-500",
  launch: "bg-emerald-500",
  after: "bg-rose-500",
};

/* The community's full Getting Started journey — a phased, module-aware checklist that
   supersedes the old setup card. Auto-detected steps tick themselves; manual steps
   (physical signage, "briefed the board", etc.) are tap-to-acknowledge. Collapsible,
   hides when complete/dismissed, and can be switched off per community in Settings. */
export function GettingStartedCard({ tenantId, onNavigate }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/golive`).then((r) => setData(r.data)).catch(() => {});
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  if (!data || !data.enabled || data.dismissed || data.complete) return null;

  const toggle = async (key, next) => {
    setBusy(key);
    try { await api.post(`/tenants/${tenantId}/golive`, { [key]: next }); load(); }
    finally { setBusy(""); }
  };
  // Auto-detected items: clicking sets a manual override. When the new value matches what we'd
  // auto-detect, we clear the override instead (so it snaps back to tracking reality).
  const toggleAuto = async (s) => {
    const next = !s.done;
    const value = next === s.auto ? null : next;
    setBusy(s.key);
    try { await api.post(`/tenants/${tenantId}/golive`, { override: { key: s.key, value } }); load(); }
    finally { setBusy(""); }
  };
  const resetAuto = async (key) => {
    setBusy(key);
    try { await api.post(`/tenants/${tenantId}/golive`, { override: { key, value: null } }); load(); }
    finally { setBusy(""); }
  };
  const dismiss = async () => {
    setData((d) => ({ ...d, dismissed: true }));
    try { await api.post(`/tenants/${tenantId}/golive`, { dismissed: true }); } catch { /* ignore */ }
  };

  const pct = data.total ? Math.round((data.done / data.total) * 100) : 0;

  return (
    <section data-testid="getting-started-card" className="rounded-2xl border border-sand-200 bg-white overflow-hidden">
      <div className="px-5 py-4 flex items-center gap-3 border-b border-sand-100 bg-gradient-to-r from-emerald-50/60 to-transparent">
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-emerald-100 text-emerald-600 shrink-0">
          <Rocket className="h-4.5 w-4.5" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading font-bold text-ink text-sm leading-tight">Get your {V.lc("org")} live</h2>
          <p className="text-xs text-ink/55 mt-0.5">Set up, test, spread the word, launch — everything to go from zero to live.</p>
        </div>
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <div className="w-28 h-1.5 rounded-full bg-sand-100 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
          <span data-testid="gs-progress" className="text-xs font-bold text-ink/70 tabular-nums w-10">{data.done}/{data.total}</span>
        </div>
        <button data-testid="gs-collapse" onClick={() => setOpen((o) => !o)} className="text-ink/40 hover:text-ink transition-colors">
          {open ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>
        <button data-testid="gs-dismiss" onClick={dismiss} title="Hide — you can turn this back on in Settings" className="text-ink/40 hover:text-ink transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <div className="divide-y divide-sand-100">
          {data.phases.map((ph, pi) => (
            <div key={ph.id} data-testid={`gs-phase-${ph.id}`} className="px-5 py-3.5">
              <div className="flex items-center gap-2.5 mb-2.5">
                <span className={`grid place-items-center h-5 w-5 rounded-full text-[10px] font-bold text-white ${PHASE_ACCENT[ph.id] || "bg-ink"}`}>{pi + 1}</span>
                <span className="text-[11px] font-bold uppercase tracking-wide text-ink">{ph.title}</span>
                <span className="text-xs text-ink/45">{ph.subtitle}</span>
                <span className="ml-auto text-[11px] font-semibold text-ink/45 tabular-nums">{ph.done}/{ph.total}</span>
              </div>
              <div className="space-y-0.5">
                {ph.items.map((s) => (
                  <div key={s.key} data-testid={`gs-item-${s.key}`} className="group flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-sand-50 transition-colors">
                    {s.manual ? (
                      <button data-testid={`gs-toggle-${s.key}`} disabled={busy === s.key} onClick={() => toggle(s.key, !s.done)} className="shrink-0" title={s.done ? "Mark as not done" : "Mark as done"}>
                        {busy === s.key ? <Loader2 className="h-5 w-5 text-ink/40 animate-spin" />
                          : s.done ? <CheckCircle2 className="h-5 w-5 text-emerald-500" strokeWidth={2} />
                          : <Circle className="h-5 w-5 text-ink/25 hover:text-emerald-500 transition-colors" strokeWidth={2} />}
                      </button>
                    ) : (
                      <button data-testid={`gs-toggle-${s.key}`} disabled={busy === s.key} onClick={() => toggleAuto(s)} className="shrink-0"
                        title={s.overridden ? "Set manually — click to change" : s.done ? "Auto-detected as done — click to un-check" : "Not detected yet — click to mark done"}>
                        {busy === s.key ? <Loader2 className="h-5 w-5 text-ink/40 animate-spin" />
                          : s.done ? <CheckCircle2 className="h-5 w-5 text-emerald-500" strokeWidth={2} />
                          : <Circle className="h-5 w-5 text-ink/25 hover:text-emerald-500 transition-colors" strokeWidth={2} />}
                      </button>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className={`text-sm font-medium ${s.done ? "line-through text-ink/40" : "text-ink"}`}>{s.label}</span>
                      {s.overridden && (
                        <button data-testid={`gs-reset-${s.key}`} onClick={() => resetAuto(s.key)}
                          className="ml-2 align-middle inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 hover:bg-amber-100 transition-colors"
                          title="This was set manually — reset to automatic detection">
                          <RotateCcw className="h-2.5 w-2.5" /> manual · reset
                        </button>
                      )}
                      {!s.done && <span className="block text-xs text-ink/50 mt-0.5">{s.hint}</span>}
                    </span>
                    {!s.done && s.link && (
                      <button data-testid={`gs-go-${s.key}`} onClick={() => onNavigate?.(s.link)}
                        className="shrink-0 inline-flex items-center gap-1 text-emerald-700 text-xs font-bold opacity-70 group-hover:opacity-100 hover:gap-1.5 transition-all">
                        Open <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
